/**
 * MetricCard component for displaying dashboard metrics.
 *
 * A reusable card component that displays a metric value with a label.
 * Uses DaisyUI styling for consistent appearance.
 */

import React from 'react';

interface MetricCardProps {
  /** Label text for the metric */
  label: string;
  /** Numeric value to display */
  value: number | string;
  /** Optional CSS class for custom styling */
  className?: string;
}

/**
 * Reusable metric card component.
 *
 * Displays a metric value in a card format with DaisyUI styling.
 * The value is formatted as a number (e.g., 1234) or can be a string.
 *
 * @param props - Component props
 * @returns Metric card UI element
 */
export const MetricCard: React.FC<MetricCardProps> = ({ label, value, className = '' }) => {
  // Format number with thousand separators if it's a number
  const formattedValue = typeof value === 'number' ? value.toLocaleString() : value;

  return (
    <div className={`card bg-[#1E1E1E] shadow-md rounded-2xl w-full h-[178px] ${className}`}>
      <div className="card-body flex flex-col">
        <h3 className="text-lg font-semibold mb-12">{label}</h3>
        <p className="text-3xl font-bold mt-auto">{formattedValue}</p>
      </div>
    </div>
  );
};
